import type { ViewController } from '@presentation/shell/NexxusShell';
import { resolveCurrentSalarie } from '@application/auth/UserSalarieLinkService';
import { CoffreFortStorageService } from '@application/persistence/CoffreFortStorageService';
import { ToastService } from '@presentation/components/Toast';
import { CongesService } from '@domain/rh/CongesService';
import { loadStcConfig } from '@application/rh/StcConfig';
import { creerDemandeConge, listConges } from '@application/rh/CongesDirectoryService';
import { downloadBlob } from '@presentation/shell/download';
import { fmtFCFA, fmtPeriode, fmtDate, esc } from '@presentation/shell/format';
import { buildBulletinHTML } from '@presentation/print/bulletinTemplate';
import { previewDocument } from '@presentation/print/PdfService';
import { FormHelper } from '@presentation/components/FormHelper';
import { onExclusive } from '@presentation/shell/viewDom';
import { openFormModal } from '@presentation/shell/openFormModal';
import { renderCongeForm } from '@presentation/views/forms/CongeFormView';
import { CongeDemandeSchema } from '@shared/validation/CongeSchema';
import type { StatutConge, TypeConge } from '@shared/types/Conge';
import { loadRegister } from '@presentation/shell/localCrud';
import { activePrivacyPolicy, type PrivacyPolicy } from '@domain/legal/PrivacyPolicy';

/** Ouvre la politique active (espace personnel + coffre). */
export async function ensureOpenActivePrivacyPolicy(
  ctx: Parameters<ViewController>[1],
  host: HTMLElement,
): Promise<void> {
  const tid = ctx.tenantContext.tryGetTenant()?.id ?? 'default';
  const policies = await loadRegister<PrivacyPolicy>(tid, 'legal.privacy_policies');
  const active = activePrivacyPolicy(policies);
  if (!active) {
    ToastService.info('Aucune politique de confidentialité publiée');
    return;
  }
  openFormModal(host, { title: `Politique ${active.version}`, size: 'lg' }, (modalHost, close) => {
    modalHost.innerHTML = `<p style="font-size:12px;color:var(--gris)">Entrée en vigueur : ${esc(active.effective_date)}</p>
      <div style="white-space:pre-wrap;font-size:13px;line-height:1.55;margin-top:8px">${esc(active.content)}</div>
      <div style="margin-top:16px;text-align:right"><button type="button" class="btn btn-primary" data-close>Fermer</button></div>`;
    modalHost.querySelector('[data-close]')?.addEventListener('click', close);
  });
}

export const selfBulletinsController: ViewController = async (section, ctx, shell) => {
  (window as unknown as Record<string, unknown>)['openActivePrivacyPolicy'] = () => {
    void ensureOpenActivePrivacyPolicy(ctx, section);
  };
  const [salaries, bulletins] = await Promise.all([ctx.salaries.list(), ctx.bulletins.list()]);
  const tenant = ctx.tenantContext.tryGetTenant();
  const sal = await resolveCurrentSalarie(ctx, salaries);
  const isManager = shell.permission === 'admin' || shell.permission === 'rh';
  // Admin / RH : tous les bulletins (pas de message d’accès refusé / liste vide forcée).
  const mine = (
    isManager
      ? bulletins.filter((b) => b.statut !== 'annule')
      : sal
        ? bulletins.filter((b) => b.salarie_id === sal.id)
        : []
  ).sort((a, b) => b.periode.localeCompare(a.periode));

  const title = section.querySelector('h1');
  if (title && isManager) title.textContent = 'Bulletins (vue RH)';

  const tbody = section.querySelector<HTMLElement>('#self-bulletins-tbody');
  const empty = section.querySelector<HTMLElement>('#self-bulletins-empty');
  if (empty) empty.style.display = mine.length === 0 ? '' : 'none';
  if (tbody) {
    tbody.innerHTML = mine
      .map(
        (b) => `<tr>
        <td>${esc(fmtPeriode(b.periode))}</td>
        <td class="num">${esc(fmtFCFA(b.brut_imposable))}</td>
        <td class="num">${esc(fmtFCFA(b.net_a_payer))}</td>
        <td class="num"><button class="btn btn-ghost btn-sm" data-self-bulletin="${esc(b.id)}">Voir</button></td>
      </tr>`,
      )
      .join('');
  }

  onExclusive(tbody, 'click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-self-bulletin]');
    if (!btn) return;
    const b = mine.find((x) => x.id === btn.dataset.selfBulletin);
    if (!b) return;
    const fiche = salaries.find((s) => s.id === b.salarie_id) ?? sal;
    if (!fiche) {
      ToastService.error('Fiche salarié introuvable pour ce bulletin');
      return;
    }
    const filename = `Bulletin_${fiche.nom}_${fiche.prenom}_${b.periode}.pdf`.replace(/\s+/g, '_');
    previewDocument(
      buildBulletinHTML(b, fiche, tenant),
      filename,
      `Bulletin · ${fmtPeriode(b.periode)}`,
    );
  });
};

export const selfCongesController: ViewController = async (section, ctx, shell) => {
  (window as unknown as Record<string, unknown>)['openActivePrivacyPolicy'] = () => {
    void ensureOpenActivePrivacyPolicy(ctx, section);
  };
  const salaries = await ctx.salaries.list();
  const sal = await resolveCurrentSalarie(ctx, salaries);
  const tenantId = ctx.tenantContext.tryGetTenant()?.id ?? sal?.tenant_id ?? 'default';
  const [allConges, stc] = sal
    ? await Promise.all([listConges(tenantId), loadStcConfig(tenantId)])
    : [[], undefined];
  const conges = sal ? allConges.filter((c) => c.salarie_id === sal.id) : [];
  const solde = sal ? CongesService.calculerSolde(sal, conges, new Date(), stc?.conges) : 0;

  const soldeEl = section.querySelector('#self-conges-solde');
  if (soldeEl) soldeEl.textContent = `${solde} jours`;

  const typeLabel: Record<TypeConge, string> = {
    annuel: 'Annuel',
    maternite: 'Maternité',
    paternite: 'Paternité',
    maladie: 'Maladie',
    evenement_familial: 'Événement familial',
    sans_solde: 'Sans solde',
    autre: 'Autre',
  };
  const statutLabel: Record<StatutConge, string> = {
    demande: 'En attente',
    valide: 'Approuvé',
    refuse: 'Refusé',
    pris: 'Pris',
    annule: 'Annulé',
  };

  const tbody = section.querySelector<HTMLElement>('#self-conges-tbody');
  if (tbody) {
    tbody.innerHTML =
      conges.length === 0
        ? '<tr><td colspan="5" style="color:var(--gris);padding:14px;">Aucune demande.</td></tr>'
        : conges
            .slice()
            .sort((a, b) => b.cree_le.localeCompare(a.cree_le))
            .map(
              (c) => `<tr>
        <td>${esc(typeLabel[c.type])}</td>
        <td>${esc(fmtDate(c.date_debut))}</td>
        <td>${esc(fmtDate(c.date_fin))}</td>
        <td class="num">${c.jours}</td>
        <td>${esc(statutLabel[c.statut])}</td>
      </tr>`,
            )
            .join('');
  }

  const w = window as unknown as Record<string, unknown>;
  w['openCongeSelfModal'] = () => {
    if (!sal) {
      ToastService.error('Aucune fiche salarié n’est rattachée à ce compte');
      return;
    }
    openFormModal(section, { title: 'Demander un congé', size: 'md' }, (host, close) => {
      host.innerHTML = renderCongeForm({ salarieId: sal.id });
      host.querySelector('[data-cancel]')?.addEventListener('click', close);
      const form = host.querySelector<HTMLFormElement>('#conge-form')!;
      FormHelper.bindSubmit(form, CongeDemandeSchema, async (data) => {
        await creerDemandeConge(tenantId, sal, {
          type: data.type,
          date_debut: data.date_debut,
          date_fin: data.date_fin,
          motif: data.motif,
        });
        ToastService.success('Demande envoyée');
        close();
        await selfCongesController(section, ctx, shell);
      });
    });
  };
};

const TYPE_CLASS: Record<string, string> = {
  Contrats: 'sd-type-contrat',
  Bulletins: 'sd-type-bulletin',
  Attestations: 'sd-type-attestation',
  Déclarations: 'sd-type-autre',
  Entreprise: 'sd-type-autre',
  Autres: 'sd-type-autre',
};

const CATEGORIES = [
  'Tous',
  'Contrats',
  'Bulletins',
  'Attestations',
  'Entreprise',
  'Autres',
] as const;

/**
 * Documents visibles : ceux du salarié + documents d'entreprise non nominatifs (A-28).
 * Un document sans rattachement et hors catégorie « Entreprise » reste privé :
 * il concerne potentiellement un autre salarié.
 */
export function filtrerDocsSelfService<T extends { salarie_id: string | null; category: string }>(
  docs: readonly T[],
  salarieId: string,
): T[] {
  return docs.filter(
    (d) => d.salarie_id === salarieId || (!d.salarie_id && d.category === 'Entreprise'),
  );
}

export const selfDocsController: ViewController = async (section, ctx) => {
  (window as unknown as Record<string, unknown>)['openActivePrivacyPolicy'] = () => {
    void ensureOpenActivePrivacyPolicy(ctx, section);
  };
  const salaries = await ctx.salaries.list();
  const sal = await resolveCurrentSalarie(ctx, salaries);
  const tid = ctx.tenantContext.tryGetTenant()?.id ?? 'default';
  const docs = await CoffreFortStorageService.listDocuments(ctx, tid);
  const visibles = sal ? filtrerDocsSelfService(docs, sal.id) : [];
  let filtre: string = 'Tous';

  const count = section.querySelector('#self-docs-count');
  const filters = section.querySelector<HTMLElement>('#self-docs-filters');
  const grid = section.querySelector<HTMLElement>('#self-docs-list');

  const paint = (): void => {
    const filtered = filtre === 'Tous' ? visibles : visibles.filter((d) => d.category === filtre);
    if (count) count.textContent = String(filtered.length);
    if (filters) {
      // A-27 — filtres par type conservés à l'affichage
      filters.innerHTML = CATEGORIES.map(
        (c) =>
          `<button type="button" class="btn btn-sm ${filtre === c ? 'btn-primary' : 'btn-ghost'}" data-doc-filter="${esc(c)}">${esc(c)}</button>`,
      ).join(' ');
    }
    if (!grid) return;
    if (filtered.length === 0) {
      grid.innerHTML =
        '<div class="empty" style="padding:24px;"><p>Aucun document disponible.</p></div>';
      return;
    }
    const byCat = new Map<string, typeof filtered>();
    for (const d of filtered) {
      const cat = d.category || 'Autres';
      const arr = byCat.get(cat) ?? [];
      arr.push(d);
      byCat.set(cat, arr);
    }
    grid.innerHTML = [...byCat.entries()]
      .map(
        ([cat, list]) => `<div class="self-docs-group" style="margin-bottom:18px;">
          <h3 style="font-size:13px;margin:0 0 10px;">${esc(cat)} (${list.length})</h3>
          <div style="display:grid;gap:10px;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));">
          ${list
            .map(
              (d) => `<div class="sd-doc-card ${TYPE_CLASS[d.category] ?? 'sd-type-autre'}">
            <div class="sd-doc-accent"></div>
            <div class="sd-doc-head"><div class="sd-doc-info">
              <div class="sd-doc-name">${esc(d.name)}</div>
              <div class="sd-doc-type">${esc(d.category)}${!d.salarie_id ? ' · entreprise' : ''}</div>
            </div></div>
            <div class="sd-doc-meta"><span class="sd-doc-meta-item">Ajouté le <strong>${esc(fmtDate(d.uploadedAt))}</strong></span></div>
            <div class="sd-doc-actions"><button class="btn btn-sm btn-secondary" data-self-doc="${esc(d.id)}">Télécharger</button></div>
          </div>`,
            )
            .join('')}
          </div></div>`,
      )
      .join('');
  };
  paint();

  onExclusive(filters, 'click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-doc-filter]');
    if (!btn?.dataset.docFilter) return;
    filtre = btn.dataset.docFilter;
    paint();
  });

  onExclusive(grid, 'click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-self-doc]');
    const doc = visibles.find((d) => d.id === btn?.dataset.selfDoc);
    if (!doc) return;
    void (async () => {
      try {
        downloadBlob(await CoffreFortStorageService.getDownloadBlob(doc, tid), doc.name);
      } catch (error) {
        ToastService.error(`Téléchargement impossible : ${(error as Error).message}`);
      }
    })();
  });
};
